// ./src/QrScanner/scanImage.ts
//
// Image preparation for the decoder (ROADMAP #65 e, 2026-09-28; decoder switched to zxing-wasm 2026-09-29).
//
// ⚠ MEASURED, NOT GUESSED: on a synthetic harness (a portrait phone frame, codes decoded through the
// scanner's own crop) the flat-field step recovered codes under a shadow that ALSO had weak contrast —
// the case the owner hit on a shaded shelf — at ~2ms, with no wrong reads introduced.

/**
 * Flat-field correction: divide each pixel by a heavily blurred copy of the crop (an estimate of
 * the LIGHTING) so a gradual shadow cancels out and the bars keep their contrast everywhere.
 * The blur is a downscale-then-upscale, which every browser does natively and fast.
 */
export const flattenLighting = (src: HTMLCanvasElement, work: { small: HTMLCanvasElement; light: HTMLCanvasElement; out: HTMLCanvasElement }): HTMLCanvasElement | null => {
  const w = src.width;
  const h = src.height;
  const sw = Math.max(1, Math.round(w / 24));
  const sh = Math.max(1, Math.round(h / 24));

  work.small.width = sw;
  work.small.height = sh;
  work.light.width = w;
  work.light.height = h;
  work.out.width = w;
  work.out.height = h;

  const smallCtx = work.small.getContext("2d");
  const lightCtx = work.light.getContext("2d", { willReadFrequently: true });
  const outCtx = work.out.getContext("2d", { willReadFrequently: true });

  if (!smallCtx || !lightCtx || !outCtx) {
    return null;
  }

  smallCtx.imageSmoothingEnabled = true;
  smallCtx.drawImage(src, 0, 0, sw, sh);
  lightCtx.imageSmoothingEnabled = true;
  lightCtx.drawImage(work.small, 0, 0, w, h);
  outCtx.drawImage(src, 0, 0);

  const image = outCtx.getImageData(0, 0, w, h);
  const light = lightCtx.getImageData(0, 0, w, h).data;
  const px = image.data;

  for (let i = 0; i < px.length; i += 4) {
    const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
    const bg = 0.299 * light[i] + 0.587 * light[i + 1] + 0.114 * light[i + 2] || 1;
    const v = Math.max(0, Math.min(255, (lum / bg) * 200));
    px[i] = v;
    px[i + 1] = v;
    px[i + 2] = v;
  }

  outCtx.putImageData(image, 0, 0);

  return work.out;
};

/**
 * The crop straightened by `degrees` onto `out` (grown to hold it), so the bars of a code held at a
 * slant run upright for the decoder. zxing-cpp's own rotation only tries multiples of 90°; measured,
 * ±20° and ±40° attempts recovered most slanted 1D codes. The new corners are filled grey, not
 * black, so they do not read as bars.
 */
export const rotateCanvas = (src: HTMLCanvasElement, degrees: number, out: HTMLCanvasElement): HTMLCanvasElement | null => {
  const rad = (degrees * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));

  out.width = Math.ceil(src.width * cos + src.height * sin);
  out.height = Math.ceil(src.width * sin + src.height * cos);

  const ctx = out.getContext("2d", { willReadFrequently: true });

  if (!ctx) {
    return null;
  }

  ctx.fillStyle = "#808080";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.translate(out.width / 2, out.height / 2);
  ctx.rotate(rad);
  ctx.drawImage(src, -src.width / 2, -src.height / 2);
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  return out;
};
